<template>
  <div class="mpf-grid-row">
    <div class="mpf-grid-column-two-thirds">
      <p v-if="apiLoading && !messages.length">Loading your message...</p>

      <template v-else>
        <template v-for="message in messages" :key="message.id">
          <mpf-message-card
            :id="`message-${message.id}`"
            :direction="directionFor(message)"
            :title="message.title"
            :sent-at="message.createdDateTime"
            :sent-at-prefix="prefixFor(message)"
          >
            <mpf-summary-list
              v-if="message.isStructuredCommunication"
              :rows="rowsFor(message)"
              no-border
            />
            <!-- eslint-disable-next-line vue/no-v-html -- server-sanitised HTML from the API -->
            <div
              v-else-if="message.bodyHtml"
              class="mpf-message-card__text"
              v-html="message.bodyHtml"
            ></div>
            <p v-else class="mpf-message-card__text">{{ message.body }}</p>

            <mpf-attachment-list
              v-if="message.attachments?.length"
              :items="message.attachments"
              :heading-level="3"
            />
          </mpf-message-card>

          <mpf-button
            v-if="canReplyTo(message) && replyingToId !== message.id"
            label="Reply"
            variant="secondary"
            class="mpf-button--auto-width mpf-u-margin-bottom-6"
            @click="openReplyTo(message.id)"
          />

          <!-- The form opens directly under the message being answered, so it stays in view. -->
          <section
            v-if="replyingToId === message.id"
            :id="`reply-${message.id}`"
            :ref="(el) => registerReplySection(message.id, el)"
            class="mpf-u-margin-bottom-6"
          >
            <h2 class="mpf-heading-m">Reply</h2>

            <mpf-warning-callout
              v-if="replyWarning"
              :heading="replyWarning.title"
              :heading-level="3"
            >
              <p>{{ replyWarning.message }}</p>
            </mpf-warning-callout>

            <mpf-form
              :fields="formMessageReply.fields"
              :form-errors="formErrors"
              submit-label="Send reply"
              @submit="(e) => handleReplySubmit(e)"
            />

            <mpf-button
              label="Cancel"
              variant="secondary"
              class="mpf-button--auto-width mpf-u-margin-top-4"
              @click="closeReply()"
            />
          </section>
        </template>

        <p v-if="!canReply && showConversationView" class="mpf-u-margin-top-4">
          This conversation is closed, so you cannot reply to it. Contact your GP practice if you
          need to get in touch.
        </p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue';
  import { useRoute } from 'vue-router';
  import { storeToRefs } from 'pinia';

  import MpfMessageCard from '../components/MpfMessageCard/MpfMessageCard.vue';
  import MpfSummaryList from '../components/MpfSummaryList/MpfSummaryList.vue';
  import MpfAttachmentList from '../components/MpfAttachmentList/MpfAttachmentList.vue';
  import MpfButton from '../components/MpfButton/MpfButton.vue';
  import MpfWarningCallout from '../components/MpfWarningCallout/MpfWarningCallout.vue';
  import MpfForm from '../components/MpfForm/MpfForm.vue';
  import { router } from '../../router';
  import { formMessageReply } from '../../data/forms';
  import { useMessageThreadStore } from '../../stores/useMessageThreadStore';
  import { useErrorStore } from '../../stores/useErrorStore';
  import type { ThreadMessage } from '../../types/message';

  type FormError = { name: string; errors: string[] };

  const route = useRoute();
  const messageThreadStore = useMessageThreadStore();
  const errorStore = useErrorStore();
  const { messages, canReply, replyWarning, showConversationView, latestMessageId, apiLoading } =
    storeToRefs(messageThreadStore);

  const formErrors = ref<FormError[]>([]);
  const replyingToId = ref<string | null>(null);
  const replySections = new Map<string, HTMLElement>();

  const messageId = computed(() => String(route.params.messageId ?? ''));

  // The API sends INBOUND/OUTBOUND; the card's prop is lowercase.
  const directionFor = (message: ThreadMessage) =>
    message.direction === 'OUTBOUND' ? ('outbound' as const) : ('inbound' as const);

  const prefixFor = (message: ThreadMessage) =>
    message.direction === 'OUTBOUND' ? 'You sent' : 'Sent';

  const rowsFor = (message: ThreadMessage) =>
    (message.responseDetails ?? []).map(({ question, answer }) => ({
      key: question,
      value: answer,
    }));

  // Anchored to the newest message rather than to direction: the API reports direction
  // from the clinician's side, so a message from the practice arrives as 'outbound'.
  const canReplyTo = (message: ThreadMessage) =>
    canReply.value && message.id === latestMessageId.value;

  const registerReplySection = (id: string, el: unknown) => {
    if (el instanceof HTMLElement) replySections.set(id, el);
    else replySections.delete(id);
  };

  const openReplyTo = async (id: string) => {
    replyingToId.value = id;
    formErrors.value = [];

    await nextTick();
    replySections.get(id)?.scrollIntoView({ block: 'nearest' });
    replySections.get(id)?.querySelector('textarea')?.focus();
  };

  const closeReply = () => {
    replyingToId.value = null;
    formErrors.value = [];
  };

  const handleReplySubmit = async (data: any) => {
    if (!data.valid) return;

    messageThreadStore.replyMessage = data.values.replyMessage;
    messageThreadStore.attachments = data.values.attachments ?? [];

    const result = await messageThreadStore.submitReply();

    if (result.ok) {
      formErrors.value = [];
      await router.push(`/messages/${messageId.value}/reply-sent`);
      return;
    }

    errorStore.setErrors(result.errors);
    const message = errorStore.getFirstMessage();
    formErrors.value = [
      {
        name: 'replyMessage',
        errors: [message && message.trim() ? message : 'Your reply could not be sent.'],
      },
    ];
  };

  onMounted(async () => {
    // ENG-21791 resolveThread, not loadThread: the throwing client's 401 reached the global
    // handler and bounced to /error before the guard could redirect
    if (!messages.value.length) {
      messageThreadStore.setMessageId(messageId.value);
      await messageThreadStore.resolveThread(messageId.value);
    }

    // ENG-21798 No mark-opened endpoint exists on the V2 controller yet, so calling it
    // 404s and the global error handler bounces the patient off the page.
    await nextTick();

    // /reply (and the V1 SMS link) lands here with #reply — open the form on the
    // latest message from the practice rather than making the patient hunt for it.
    if (route.hash === '#reply' && canReply.value) {
      const latestInbound = [...messages.value].reverse().find((m) => canReplyTo(m));
      if (latestInbound) {
        await openReplyTo(latestInbound.id);
        return;
      }
    }

    // Otherwise scroll to the newest message, as V1 did.
    if (latestMessageId.value) {
      document.getElementById(`message-${latestMessageId.value}`)?.scrollIntoView({ block: 'end' });
    }
  });
</script>
